import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Users, CalendarClock, HardHat, FileText, SlidersHorizontal, Tag, Building2 } from "lucide-react";
import { toast } from "sonner";

const TIPO_LABEL = { testo: "Testo breve", testo_lungo: "Testo lungo", numero: "Numero", data: "Data" };

function CampiSottocommessaCard() {
  const [items, setItems] = useState([]);
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("testo");

  const load = () => api.get("/campi_sottocommessa").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!nome.trim()) return toast.error("Inserisci un'etichetta");
    try { await api.post("/campi_sottocommessa", { nome, tipo }); setNome(""); setTipo("testo"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/campi_sottocommessa/${id}`); load(); };

  return (
    <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden">
      <div className="p-5 border-b border-slate-100 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-violet-50 text-violet-600"><SlidersHorizontal className="h-[18px] w-[18px]" /></div>
        <div>
          <h3 className="font-heading font-semibold text-slate-900 leading-none">Campi sottocommessa</h3>
          <p className="text-xs text-slate-400 mt-1">Attributi aggiuntivi mostrati nella scheda Dettagli</p>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex gap-2">
          <Input data-testid="campo-sub-input" value={nome} onChange={(e) => setNome(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Etichetta (es. Via)" />
          <Select value={tipo} onValueChange={setTipo}>
            <SelectTrigger data-testid="campo-sub-tipo" className="w-40 flex-shrink-0 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TIPO_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="campo-sub-add" onClick={add} className="bg-blue-700 hover:bg-blue-800 flex-shrink-0"><Plus className="h-4 w-4" /></Button>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Nessun campo. Aggiungine uno sopra.</p>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
            {items.map((it) => (
              <div key={it.id} data-testid={`campo-sub-item-${it.id}`} className="flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-slate-50">
                <span className="text-sm text-slate-700 truncate flex items-center gap-2">{it.nome}
                  <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 font-normal text-[10px]">{TIPO_LABEL[it.tipo] || it.tipo}</Badge>
                </span>
                <ConfirmDelete title="Eliminare campo?" description={`"${it.nome}" verrà rimosso dalle schede.`} actionTestid={`campo-sub-confirm-${it.id}`} onConfirm={() => remove(it.id)}>
                  <button data-testid={`campo-sub-del-${it.id}`} className="text-slate-400 hover:text-rose-600 flex-shrink-0"><Trash2 className="h-4 w-4" /></button>
                </ConfirmDelete>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function AnagList({ title, subtitle, icon: Icon, endpoint, tint, testidKey }) {
  const [items, setItems] = useState([]);
  const [nome, setNome] = useState("");

  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    if (!nome.trim()) return toast.error("Inserisci un nome");
    try {
      await api.post(`/${endpoint}`, { nome });
      setNome("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    await api.delete(`/${endpoint}/${id}`);
    load();
  };

  return (
    <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden">
      <div className="p-5 border-b border-slate-100 flex items-center gap-3">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tint}`}>
          <Icon className="h-[18px] w-[18px]" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-slate-900 leading-none">{title}</h3>
          <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex gap-2">
          <Input data-testid={`${testidKey}-input`} value={nome} onChange={(e) => setNome(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nuovo nome…" />
          <Button data-testid={`${testidKey}-add`} onClick={add} className="bg-blue-700 hover:bg-blue-800 flex-shrink-0">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Nessun elemento. Aggiungine uno sopra.</p>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
            {items.map((it) => (
              <div key={it.id} data-testid={`${testidKey}-item-${it.id}`} className="flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-slate-50">
                <span className="text-sm text-slate-700 truncate">{it.nome}</span>
                <ConfirmDelete title="Eliminare?" description={`"${it.nome}" verrà rimosso.`} actionTestid={`${testidKey}-confirm-${it.id}`} onConfirm={() => remove(it.id)}>
                  <button data-testid={`${testidKey}-del-${it.id}`} className="text-slate-400 hover:text-rose-600 flex-shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </ConfirmDelete>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function SubappaltatoriCard() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ nome: "", piva: "", referente: "", telefono: "" });

  const load = () => api.get("/subappaltatori").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.nome.trim()) return toast.error("Inserisci il nome dell'azienda");
    try {
      await api.post("/subappaltatori", form);
      setForm({ nome: "", piva: "", referente: "", telefono: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/subappaltatori/${id}`); load(); };

  return (
    <Card className="rounded-md border-slate-200 shadow-sm overflow-hidden">
      <div className="p-5 border-b border-slate-100 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-amber-50 text-amber-600"><HardHat className="h-[18px] w-[18px]" /></div>
        <div>
          <h3 className="font-heading font-semibold text-slate-900 leading-none">Aziende subappaltatrici</h3>
          <p className="text-xs text-slate-400 mt-1">Selezionabili nelle righe di subappalto</p>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Input data-testid="subapp-nome-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ragione sociale *" />
          <Input data-testid="subapp-piva-input" value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} placeholder="P.IVA" />
          <Input data-testid="subapp-referente-input" value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} placeholder="Referente" />
          <div className="flex gap-2">
            <Input data-testid="subapp-telefono-input" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="Telefono" />
            <Button data-testid="subapp-add" onClick={add} className="bg-blue-700 hover:bg-blue-800 flex-shrink-0"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">Nessuna azienda. Aggiungine una sopra.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((s) => (
              <div key={s.id} data-testid={`subapp-item-${s.id}`} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 text-sm">{s.nome}</div>
                  <div className="text-xs text-slate-400">{[s.piva, s.referente, s.telefono].filter(Boolean).join(" · ") || "—"}</div>
                </div>
                <ConfirmDelete title="Eliminare azienda?" description={`"${s.nome}" verrà rimossa.`} actionTestid={`subapp-confirm-${s.id}`} onConfirm={() => remove(s.id)}>
                  <button data-testid={`subapp-del-${s.id}`} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </ConfirmDelete>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function Anagrafiche() {
  return (
    <>
      
      <div className="flex-1 overflow-auto px-6 py-6 lg:px-8 space-y-6">
        <PageHeader title="Anagrafiche" subtitle="Squadre, Competenze, Tipi documento, Tipo, Committenti e Aziende subappaltatrici" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
          <AnagList title="Squadre" subtitle="Team operativi selezionabili nelle righe" icon={Users}
            endpoint="squadre" tint="bg-blue-50 text-blue-700" testidKey="squadra" />
          <AnagList title="Competenze" subtitle="Competenze selezionabili nelle righe" icon={CalendarClock}
            endpoint="competenze" tint="bg-emerald-50 text-emerald-600" testidKey="competenza" />
          <AnagList title="Tipi documento" subtitle="Categorie per i documenti delle sottocommesse" icon={FileText}
            endpoint="tipi_documento" tint="bg-amber-50 text-amber-600" testidKey="tipo-documento" />
          <AnagList title="Tipo" subtitle="Valori selezionabili nella colonna Tipo delle sottocommesse" icon={Tag}
            endpoint="tipi_commessa" tint="bg-rose-50 text-rose-600" testidKey="tipo-commessa" />
          <AnagList title="Committenti" subtitle="Committenti selezionabili nelle fatture" icon={Building2}
            endpoint="committenti" tint="bg-indigo-50 text-indigo-600" testidKey="committente" />
          <CampiSottocommessaCard />
        </div>
        <div className="max-w-4xl">
          <SubappaltatoriCard />
        </div>
      </div>
    </>
  );
}
